import { Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { GridColDef } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LinkButton from '../../components/Buttons/LinkButton';
import DialogAction from '../../components/Dialog/DialogAction';
import TableGridWithFilters from '../../components/TablegridWithFilters/TableGridWithFilters/TableGridWithFilters';
import { Administrative_Units_Status } from '../../lib/static/users/selectOptions';
import Theme from '../../Themes/main.theme';
import MovieIcon from '@mui/icons-material/Movie';
import MovieOutlinedIcon from '@mui/icons-material/MovieOutlined';
import EditIcon from '@mui/icons-material/Edit';
import PageviewOutlinedIcon from '@mui/icons-material/PageviewOutlined';
import EnableSelector from '../../components/EnableComponent/EnableSelector';
import { useSnackbar } from 'notistack';
import { FieldControllerTypes } from '../../components/form_v2/types';
import HttpService from '../../services/HttpService';
import { useTranslation } from 'react-i18next';
import UserService from '../../services/UserService';
import { PermissionsManageFilmOffice } from '../../lib/permissions';

const initialValues = {
  administrative_units: '',
  is_enabled: ''
};

const AdminUnitsDashboard = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState({ id: null, office: '', is_enabled: false });
  const [toggleStatus, setToggleStatus] = useState<boolean>(false);

  const [userInfo, setUserInfo] = useState<any>(null);

  const [administrativeUnits, setAdministrativeUnits] =
    useState<{ key: string; value: number }[]>([]);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();


  const userCanEditUnit = (unitId: any) => UserService
    .hasRole([PermissionsManageFilmOffice.PermissionsInternalFilmOfficeCreateAll])
    || (
      UserService.hasRole([PermissionsManageFilmOffice.PermissionsInternalFilmOfficeCreateMyOrg]) 
      && unitId == userInfo.administrative_unit_id
    );

  const filterFields = [
    [
      {
        control: FieldControllerTypes.autocomplete,
        type: 'autocomplete',
        label: t('Οργανικές Μονάδες'),
        name: 'administrative_units',
        filtersDesign: true,
        options: administrativeUnits
      },
      {
        control: FieldControllerTypes.select,
        label: t('Κατάσταση Οργανικής Μονάδας'),
        name: 'is_enabled',
        customLabel: true,
        filtersDesign: true,
        options: Administrative_Units_Status,
      }
    ]
  ];

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: t('Όνομα') || undefined,
      minWidth: 300,
    },
    {
      field: 'email',
      headerName: 'Email' || undefined,
      minWidth: 280
    },
    {
      field: 'phone',
      headerName: t('Τηλέφωνο') || undefined,
      minWidth: 180
    },
    {
      field: 'is_enabled',
      headerName: t('Κατάσταση Οργανικής Μονάδας') || undefined,
      minWidth: 280,
      renderCell: (params) =>
        <EnableSelector IsEnabled={params.row.is_enabled} truthfulLabel={'Ενεργή'}
          falseLabel={'Ανενεργή'} />
    },
    {
      field: 'action_admin',
      headerName: t('Ενέργειες') || undefined,
      minWidth: 170,
      sortable: false,
      renderCell: (params) => <>
        <Tooltip PopperProps={{
          disablePortal: true
        }}
        title='Προβολή'>
          <IconButton color='primary'
            onClick={() => navigate(`/admin/units/${params.row.office}/${params.row.id}`)}>
            <PageviewOutlinedIcon /></IconButton>
        </Tooltip>
        <Tooltip PopperProps={{
          disablePortal: true
        }}
        title='Επεξεργασία'>
          <IconButton disabled={!userCanEditUnit(params.row.id)} color='primary'
            onClick={() => navigate(`/admin/units/${params.row.office}/edit/${params.row.id}`)} >
            <EditIcon /></IconButton>
        </Tooltip>
        <Tooltip
          PopperProps={{
            disablePortal: true
          }}
          title={t('Αλλαγή Κατάστασης')}>
          <IconButton
            color='primary'
            disabled={!userCanEditUnit(params.row.id)}
            onClick={() => {
              setOpen(true);
              setStatus({
                id: params.row.id,
                office: params.row.office === 'ekome'
                  ? 'front_office'
                  : params.row.office,
                is_enabled: !params.row.is_enabled
              });
            }}>
            {!params.row.is_enabled
              ? <MovieIcon /> :
              <MovieOutlinedIcon />}
          </IconButton>
        </Tooltip>
      </>
    },
  ];

  const onSubmit = async () => {
    try {
      const resp = await HttpService.getAxiosClient().patch(`${process.env.REACT_APP_BACKEND_URL}/registrations/${status.office}/${status.id}`, {
        is_enabled: status.is_enabled
      });
      if (resp.status === 200) {
        setToggleStatus((toggleStatus) => !toggleStatus);
        enqueueSnackbar(`Επιτυχής αλλαγή κατάστασης του ${status.office}`, { variant: 'success' });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Παρουσιάστηκε σφάλμα, παρακαλούμε προσπαθήστε αργότερα.', { variant: 'error' });
    }
  };

  const getAdminUnits = async () => {
    const userDoeHaveAll = UserService.hasRole([
      PermissionsManageFilmOffice.PermissionsInternalFilmOfficeReadAll
    ]);
    const response = userDoeHaveAll
      ? await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/users-parametrics/administrative_unit`)
      : await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/users-parametrics/administrative_unit/my_org`);
    setAdministrativeUnits(response.data.units.sort((a: any, b: any) => a.key.localeCompare(b.key, 'el')));
  };

  const getSetSelfDetails = async () => {
    const response = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/users/user`);

    if (response.status === 200) {
      setUserInfo(response.data);
    }
  };


  useEffect(() => {
    getSetSelfDetails();
    getAdminUnits();
  }, []);
  return (
    <>
      <Box minWidth={400}>
        <Typography variant='h2' sx={{ my: 2, mt: 4 }}
          color={Theme.labelColor?.main}>
          {t('Διαχείριση Οργανικών Μονάδων')}
        </Typography>
        <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
        <Box display='flex' justifyContent='end'
          mt={5} gap={2}
          flexWrap='wrap'>
          {UserService.hasRole([PermissionsManageFilmOffice.PermissionsInternalFilmOfficeCreateAll]) && <LinkButton link='/admin/units/ekome/new'
            variant='contained' sx={{ borderRadius: 24, maxWidth: '300px', minWidth: '250px', height: '50px', backgroundColor: Theme.buttonColor?.main }}>Δημιουργία Οργανικής Μονάδας</LinkButton>
          }
          {UserService.hasRole([PermissionsManageFilmOffice.PermissionsInternalFilmOfficeCreateAll]) && <LinkButton link='/admin/units/film_office/new'
            variant='contained' sx={{ borderRadius: 24, maxWidth: '300px', minWidth: '250px', height: '50px', backgroundColor: Theme.buttonColor?.main }}>Δημιουργία Film Office</LinkButton>
          }
        </Box>
        <TableGridWithFilters
          responseDataFieldName='data'
          responseTotalCountFieldName='total_count'
          sortingMode='server'
          sortModel={[{ field: 'title', sort: 'asc' }]}
          paginationMode='server'
          initialValues={initialValues}
          filterFields={filterFields}
          columns={columns}
          resetFilters={true}
          fetchUrl={
            UserService.hasRole([PermissionsManageFilmOffice.PermissionsInternalFilmOfficeReadAll])
              ? `${process.env.REACT_APP_BACKEND_URL}/registrations/offices/`
              : `${process.env.REACT_APP_BACKEND_URL}/registrations/offices/my_org/`
          }
          reFetch={toggleStatus}
        />
        <DialogAction
          open={open} title={'Αλλαγή Κατάστασης'}
          handleClose={() => {
            setOpen(false);
          }}
          content='Είστε σίγουρος/η ότι θέλετε να αλλάξετε την κατάσταση;'
          onAccept={() => {
            onSubmit();
            setOpen(false);
          }}
        />
      </Box>
    </>
  );
};

export default AdminUnitsDashboard;